import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { btn, btnPrimary, fieldCls, Field, errMsg } from "@/components/accounting/ui";

export const AccountingSettings = ({ open, settings, email, onClose, onSaved }) => {
  const [f, setF] = useState(settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF(settings); }, [open, settings]);

  const set = (k, num) => (e) => setF((x) => ({ ...x, [k]: num ? Number(e.target.value) : e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/accounting/settings", f);
      toast.success("Einstellungen gespeichert");
      onSaved(data);
    } catch (e) { toast.error(errMsg(e, "Speichern fehlgeschlagen")); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="acc-settings-dialog">
        <DialogHeader>
          <DialogTitle>Einstellungen Buchhaltung</DialogTitle>
          <DialogDescription>Empfänger, Download-Links sowie Lösch- und Aufbewahrungsregeln.</DialogDescription>
        </DialogHeader>
        <div className={`rounded-lg px-3 py-2.5 text-xs flex gap-2 ${email.configured ? "bg-emerald-500/10 text-emerald-800" : "bg-brand/10 text-red-800"}`} data-testid="acc-settings-email-status">
          {email.configured ? <CheckCircle2 size={14} className="shrink-0" /> : <XCircle size={14} className="shrink-0" />}
          {email.configured ? `E-Mail-Dienst eingerichtet: ${email.provider}` : `E-Mail-Dienst nicht eingerichtet (${email.detail}). Der Versand bleibt deaktiviert, es wird nichts versendet.`}
        </div>
        <div className="space-y-3">
          <Field label="Name der Steuerberatung (optional)"><input value={f.advisor_name} onChange={set("advisor_name")} maxLength={120} className={fieldCls} data-testid="acc-settings-advisor-name" /></Field>
          <Field label="Buchhaltungs-E-Mail des Steuerberaters"><input type="email" value={f.advisor_email} onChange={set("advisor_email")} placeholder="noch nicht bekannt" className={fieldCls} data-testid="acc-settings-advisor-email" /></Field>
          <div className="flex items-center justify-between gap-3 pt-1">
            <span className="text-xs text-dim font-medium">Kopie jeder Versand-E-Mail an AD MOTORS</span>
            <Switch checked={f.cc_enabled} onCheckedChange={(v) => setF((x) => ({ ...x, cc_enabled: v }))} data-testid="acc-settings-cc-switch" />
          </div>
          {f.cc_enabled && <Field label="Kopie an"><input type="email" value={f.cc_email} onChange={set("cc_email")} className={fieldCls} data-testid="acc-settings-cc-email" /></Field>}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Link gültig (Tage)"><input type="number" min={1} max={30} value={f.link_days} onChange={set("link_days", true)} className={fieldCls} data-testid="acc-settings-link-days" /></Field>
            <Field label="Aufbewahrung (Jahre)"><input type="number" min={7} max={30} value={f.retention_years} onChange={set("retention_years", true)} className={fieldCls} data-testid="acc-settings-retention" /></Field>
            <Field label="Papierkorb (Tage)"><input type="number" min={7} max={365} value={f.trash_days} onChange={set("trash_days", true)} className={fieldCls} data-testid="acc-settings-trash-days" /></Field>
          </div>
          <p className="text-[11px] text-faint leading-relaxed">
            Übermittelte Belege sind bis zum Ende der Aufbewahrungsfrist gesperrt (gesetzlich mind. 7 Jahre, § 132 BAO). Gelöschte, noch nicht übermittelte Belege liegen im Papierkorb und werden nach Ablauf endgültig entfernt. Der Versand löscht keine Originale.
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={saving} className={btn} data-testid="acc-settings-cancel-btn">Abbrechen</button>
          <button type="button" onClick={save} disabled={saving} className={btnPrimary} data-testid="acc-settings-save-btn">{saving && <Loader2 size={14} className="animate-spin" />} Speichern</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountingSettings;
